import React, { FC, useEffect, useState } from 'react';
import { useI18n } from '~Hooks';
import styled from '@emotion/styled';
import { GetCompetitions_Venue_competitions } from '~Providers/CompetitionsProvider/__queries__';
import {
  GetStaffAndCategories_user_company_categories,
  GetItems_items_results,
} from '../../../__queries__';
import { useCurrency, useSelector } from '~Hooks';
import { Box } from '~Styles';
import {
  renderAllSpecificItems,
  renderAllSpecificCategories,
  hasSpecificSales,
  getCompetitionTypeLocale,
} from '../../../utils';
import {
  getStaffName,
  getCorrectValueBasedOnCompType,
} from '~Providers/CompetitionsProvider/utils';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import TopThree from '../../TopThree';
import Icon from '~ComponentsImproved/Icon';
import DollarCircle from '~Assets/icons/dollar-circle.svg';
import Trophy from '~Assets/icons/trophy.svg';
import Calendar from '~Assets/icons/calendar_month.svg';
import FlagEnd from '~Assets/icons/flag-end.svg';
import ArrowUp from '~Assets/icons/arrow_up.svg';
import ArrowDown from '~Assets/icons/arrow_down.svg';
import { handyBreakpoint } from '~Styles/breakpoints';
import { DateTime } from 'luxon';
import PrintScore from '../../PrintScore';

const PlacementWrapper = styled(Box)<{ isYou?: boolean }>`
  background-color: var(--colorBlueGray1000);
  border-radius: 8px;
  position: relative;
  border: ${({ isYou = false }) => (isYou ? '1px solid var(--colorBlueGray200)' : 'none')};
`;

const LeaderboardWrapper = styled(Box)`
  width: 100%;

  ${handyBreakpoint} {
    border-bottom: 1px solid var(--colorBlueGray800);
    padding-bottom: 24px;
  }
`;

const CompetitionSettingWrapper = styled(Box)`
  background-color: var(--colorBlueGray1000);
  border-radius: 8px;
`;

const GridContainer = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr;

  ${handyBreakpoint} {
    grid-template-columns: 1fr;
  }
`;

const Wrapper = styled(Box)<{ isFullPage: boolean }>`
  background-color: ${(props) => (props.isFullPage ? 'var(--colorBlueGray1100)' : 'transparent')};
  border-radius: ${(props) => (props.isFullPage ? '8px' : '0px')};
`;

const DirectionIconWrapper = styled.div<{ direction: 'up' | 'down' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  position: absolute;
  top: -12px;
  right: -12px;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
  background-color: ${({ direction }) =>
    direction === 'up' ? 'var(--colorUIGreen)' : 'var(--colorUIRed)'};
  animation: bounce-in 1s;
`;

interface Props {
  isHandy?: boolean;
  isFullPage?: boolean;
  competition: GetCompetitions_Venue_competitions;
  categories: GetStaffAndCategories_user_company_categories[];
  items: GetItems_items_results[];
}

// Shows a richer view of the competition.
// Including some of the settings
const LeaderboardContent: FC<Props> = ({
  isHandy,
  isFullPage,
  competition,
  categories,
  items,
}) => {
  const { i18n } = useI18n();
  const currency = useCurrency();

  const staff = useSelector((state) => state.companyStaff)?.staffs ?? [];
  const staffId = useSelector((state) => state.staff?.id);

  const [previousLeaderboard, setPreviousLeaderboard] = useState<string[]>(
    competition.score.map((person) => person.staffId).slice(3),
  );

  const isHandyBreakpoint = window.innerWidth <= 600;

  const formatDate = (date: string) => {
    const luxonDate = DateTime.fromISO(date);
    return luxonDate
      .setLocale('sv')
      .toFormat(`d MMM yyyy '${i18n('Competitions.Countdown.At')}' HH:mm`);
  };

  useEffect(() => {
    if (!competition) {
      return;
    }
    const leaderBoardIds = competition.score.map((person) => person.staffId);
    const hasChanged = JSON.stringify(previousLeaderboard) !== JSON.stringify(leaderBoardIds);
    if (hasChanged) {
      setTimeout(() => {
        setPreviousLeaderboard(competition.score.map((person) => person.staffId).slice(3));
      }, 4000);
    }
  }, [competition.score]);

  const getLeaderBoardChangeDirection = (
    id: string,
    currentLeaderboard: string[],
    prevLeaderboard: string[],
  ) => {
    if (currentLeaderboard.length === 0 || prevLeaderboard.length === 0) {
      return undefined;
    }
    const currentRank = currentLeaderboard.indexOf(id);
    const previousRank = prevLeaderboard.indexOf(id);

    if (currentRank < previousRank) {
      return 'up';
    } else if (currentRank > previousRank) {
      return 'down';
    } else {
      return undefined;
    }
  };

  const directionIcon = (direction?: 'up' | 'down') => {
    if (direction) {
      return (
        <DirectionIconWrapper direction={direction}>
          <Icon
            src={direction === 'up' ? ArrowUp : ArrowDown}
            color="#fff"
            width="20px"
            height="20px"
          />
        </DirectionIconWrapper>
      );
    } else {
      return null;
    }
  };

  const slicedScore = competition.score.slice(3);

  return (
    <Wrapper padding={isFullPage ? 'medium' : undefined} isFullPage>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Heading level={isHandyBreakpoint ? 3 : 1} color="var(--colorWhite)" bottom={24}>
          {competition.name}
        </Heading>
        <PrintScore isHandy={isHandy} competitionId={competition.id} />
      </Box>

      <GridContainer gap={isHandyBreakpoint ? 'medium' : 'large'}>
        <LeaderboardWrapper
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="medium">
          <TopThree
            isModalView
            competitionType={competition.type}
            hasOnlySpecificSales={hasSpecificSales(competition.onlySpecificSales)}
            leaderboard={competition.score}
          />
          <Box
            display="flex"
            gap="small"
            flexDirection="column"
            style={{ width: '100%', flexGrow: 1 }}>
            {slicedScore.map((score, index) => (
              <PlacementWrapper
                key={score.staffId + index}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="medium"
                isYou={score.staffId === staffId}>
                <Box display="flex" alignItems="center" gap="small">
                  <Paragraph level={isHandyBreakpoint ? 4 : 3} color="var(--colorBlueGray200)">
                    {/* Adjust to the slicing of the array for correct position in the UI */}
                    {index + 4}
                  </Paragraph>
                  <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
                    {score.staffId === staffId
                      ? i18n('Competitions.Status.You')
                      : getStaffName(score.staffId, staff, i18n)}
                  </Paragraph>
                </Box>

                <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
                  {getCorrectValueBasedOnCompType(
                    score,
                    competition.type,
                    hasSpecificSales(competition.onlySpecificSales),
                    currency.format,
                    i18n,
                  )}
                </Paragraph>
                {directionIcon(
                  getLeaderBoardChangeDirection(
                    score.staffId,
                    slicedScore.map((person) => person.staffId),
                    previousLeaderboard,
                  ),
                )}
              </PlacementWrapper>
            ))}
          </Box>
        </LeaderboardWrapper>

        <Box display="flex" flexDirection="column" gap="small">
          <CompetitionSettingWrapper
            color="var(--colorBlueGray200)"
            display="flex"
            flexDirection="column"
            gap="none"
            padding="small">
            <Box display="flex" alignItems="center" gap="xs">
              <Icon src={Trophy} color="var(--colorBlueGray200)" />
              <Heading level={5} color="var(--colorBlueGray200)">
                {getCompetitionTypeLocale(competition.type, i18n)}
              </Heading>
            </Box>
          </CompetitionSettingWrapper>

          <CompetitionSettingWrapper
            color="var(--colorBlueGray200)"
            display="flex"
            flexDirection="column"
            gap="none"
            padding="small">
            <Box display="flex" alignItems="center" gap="xs">
              <Icon src={DollarCircle} color="var(--colorBlueGray200)" />
              <Heading level={5} color="var(--colorBlueGray200)">
                {hasSpecificSales(competition.onlySpecificSales)
                  ? `${i18n('Competitions.SpecificSales.Limited.Short')}:`
                  : i18n('Competitions.SpecificSales.All')}
              </Heading>
            </Box>

            {competition.onlySpecificSales.categories.length > 0 && (
              <Paragraph level={5} color="var(--colorBlueGray200)" left={32}>
                {renderAllSpecificCategories(
                  competition.onlySpecificSales.categories,
                  categories,
                  i18n,
                )}
              </Paragraph>
            )}
            {competition.onlySpecificSales.items.length > 0 && (
              <Paragraph level={5} color="var(--colorBlueGray200)" left={32}>
                {renderAllSpecificItems(competition.onlySpecificSales.items, items, i18n)}
              </Paragraph>
            )}
          </CompetitionSettingWrapper>

          <CompetitionSettingWrapper
            color="var(--colorBlueGray200)"
            display="flex"
            flexDirection="column"
            gap="none"
            padding="small">
            <Box display="flex" alignItems="center" gap="xs">
              <Icon src={Calendar} color="var(--colorBlueGray200)" />
              <Heading level={5} color="var(--colorBlueGray200)">
                {i18n('Competitions.Countdown.StartDate')}
              </Heading>
            </Box>

            <Paragraph level={5} color="var(--colorBlueGray200)" left={32}>
              {formatDate(competition.startDate)}
            </Paragraph>
          </CompetitionSettingWrapper>

          <CompetitionSettingWrapper
            color="var(--colorBlueGray200)"
            display="flex"
            flexDirection="column"
            gap="none"
            padding="small">
            <Box display="flex" alignItems="center" gap="xs">
              <Icon src={FlagEnd} color="var(--colorBlueGray200)" />
              <Heading level={5} color="var(--colorBlueGray200)">
                {i18n('Competitions.Countdown.EndDate')}
              </Heading>
            </Box>

            <Paragraph level={5} color="var(--colorBlueGray200)" left={32}>
              {formatDate(competition.endDate)}
            </Paragraph>
          </CompetitionSettingWrapper>
        </Box>
      </GridContainer>
    </Wrapper>
  );
};

export default LeaderboardContent;
